import { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const ReportePrestamosActivosExcel = ({
  loans = [],
  filters = {},
  asesores = [],
  totalRegistros = 0,
  onComplete,
  onExcelGenerated,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);

  const formatDate = (dateString) => {
    try {
      if (!dateString) return '-';
      
      if (typeof dateString === 'string' && dateString.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
        return dateString;
      }
      
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return format(date, 'dd/MM/yyyy');
    } catch {
      return '-';
    }
  };

  const generateExcelReport = useCallback(async () => {
    if (isGenerating) return;
    setIsGenerating(true);

    try {
      if (loans.length === 0) {
        toast.warning('No hay préstamos activos para generar el reporte');
        if (onComplete) onComplete();
        return;
      }

      const reportData = loans.map((loan, index) => {
        return {
          'N°': index + 1,
          'ID Préstamo': loan.idPrestamo || '-',
          'Cliente': loan.cliente || '-',
          'Monto Préstamo': parseFloat(loan.monto_prestamo || 0),
          'Tasa de Interés (%)': parseFloat(loan.tasa_interes || 0),
          'Cuotas Pagadas': parseInt(loan.cuotas_pagadas || 0, 10),
          'Cuotas Pendientes': parseInt(loan.cuotas_pendientes || 0, 10),
          'Saldo Pendiente': parseFloat(loan.saldo_pendiente || 0),
          'Fecha Venc.': loan.fecha_ultima_cuota || '-',
          'Abonado Por': loan.abonado_por || loan.prestamo?.abonado_por || '-',
          'Teléfono': loan.telefono || '-',
          'Dirección': loan.direccion || '-',
        };
      });

      const totalMontoPrestamo = loans.reduce((sum, loan) => sum + parseFloat(loan.monto_prestamo || 0), 0);
      const totalSaldoPendiente = loans.reduce((sum, loan) => sum + parseFloat(loan.saldo_pendiente || 0), 0);
      const totalCuotasPagadas = loans.reduce((sum, loan) => sum + parseInt(loan.cuotas_pagadas || 0, 10), 0);
      const totalCuotasPendientes = loans.reduce((sum, loan) => sum + parseInt(loan.cuotas_pendientes || 0, 10), 0);

      reportData.push({
        'N°': '',
        'ID Préstamo': '',
        'Cliente': 'TOTALES',
        'Monto Préstamo': totalMontoPrestamo,
        'Tasa de Interés (%)': '',
        'Cuotas Pagadas': totalCuotasPagadas,
        'Cuotas Pendientes': totalCuotasPendientes,
        'Saldo Pendiente': totalSaldoPendiente,
        'Fecha Venc.': '',
        'Abonado Por': '',
        'Teléfono': '',
        'Dirección': '',
      });

      const filtrosData = [
        ['REPORTE DE PRÉSTAMOS ACTIVOS'],
        [''],
        ['FILTROS APLICADOS:'],
        ['Búsqueda:', filters.search || 'Sin filtro'],
        ['Asesor:', filters.asesor_id ? (asesores.find(asesor => String(asesor.id) === String(filters.asesor_id))?.nombre || 'No encontrado') : 'TODOS LOS ASESORES'],
        ['Abonado por:', filters.abonado_por || 'TODOS'],
        ['Casi por terminar:', filters.casi_por_terminar ? 'SÍ (1-2 cuotas pendientes)' : 'NO'],
        [''],
        ['RESUMEN:'],
        ['Total de préstamos activos:', totalRegistros.toString()],
        ['Préstamos en este reporte:', loans.length.toString()],
        ['Total monto de préstamos:', totalMontoPrestamo],
        ['Total saldo pendiente:', totalSaldoPendiente],
        ['Total cuotas pagadas:', totalCuotasPagadas],
        ['Total cuotas pendientes:', totalCuotasPendientes],
        ['Fecha de generación:', formatDate(new Date().toISOString())],
      ];

      const wb = XLSX.utils.book_new();

      const wsFiltros = XLSX.utils.aoa_to_sheet(filtrosData);
      wsFiltros['!cols'] = [
        { wch: 30 },
        { wch: 50 },
      ];
      XLSX.utils.book_append_sheet(wb, wsFiltros, 'Información');

      const wsData = XLSX.utils.json_to_sheet(reportData);

      const colWidths = [
        { wch: 5 },
        { wch: 12 },
        { wch: 35 },
        { wch: 18 },
        { wch: 18 },
        { wch: 16 },
        { wch: 18 },
        { wch: 18 },
        { wch: 18 },
        { wch: 20 },
        { wch: 15 },
        { wch: 40 },
      ];
      
      wsData['!cols'] = colWidths;

      const totalRowNum = reportData.length + 1;
      
      for (let row = 2; row <= totalRowNum; row++) {
        const montoPrestamoCellRef = `D${row}`;
        if (wsData[montoPrestamoCellRef] && typeof wsData[montoPrestamoCellRef].v === 'number') {
          wsData[montoPrestamoCellRef].z = '#,##0.00';
          wsData[montoPrestamoCellRef].t = 'n';
        }
        
        const tasaInteresCellRef = `E${row}`;
        if (wsData[tasaInteresCellRef] && typeof wsData[tasaInteresCellRef].v === 'number') {
          wsData[tasaInteresCellRef].z = '0.00';
          wsData[tasaInteresCellRef].t = 'n';
        }

        const saldoPendienteCellRef = `H${row}`;
        if (wsData[saldoPendienteCellRef] && typeof wsData[saldoPendienteCellRef].v === 'number') {
          wsData[saldoPendienteCellRef].z = '#,##0.00';
          wsData[saldoPendienteCellRef].t = 'n';
        }
      }

      const totalRowCells = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];
      totalRowCells.forEach(col => {
        const cellRef = col + totalRowNum;
        if (wsData[cellRef]) {
          wsData[cellRef].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "FFD700" } }
          };
          
          if ((col === 'D' || col === 'H') && typeof wsData[cellRef].v === 'number') {
            wsData[cellRef].z = '#,##0.00';
            wsData[cellRef].t = 'n';
          }
        }
      });

      XLSX.utils.book_append_sheet(wb, wsData, 'Préstamos Activos');

      const excelBuffer = XLSX.write(wb, { 
        bookType: 'xlsx', 
        type: 'array',
        cellStyles: true 
      });

      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const url = URL.createObjectURL(blob);

      if (onExcelGenerated) {
        onExcelGenerated(url);
      }

      toast.success(`Reporte Excel generado correctamente. ${loans.length} préstamos procesados.`);

    } catch (error) {
      toast.error('Error al generar el reporte Excel: ' + error.message);
    } finally {
      setIsGenerating(false);
      if (onComplete) {
        onComplete();
      }
    }
  }, [loans, filters, asesores, totalRegistros, isGenerating, onComplete, onExcelGenerated]);

  useEffect(() => {
    if (loans && loans.length > 0) {
      generateExcelReport();
    }
  }, [loans, generateExcelReport]);

  return (
    <div className="hidden">
      {isGenerating && (
        <div className="fixed top-4 right-4 bg-red-600 text-white p-3 rounded-lg shadow-lg z-50">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            Generando reporte Excel...
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportePrestamosActivosExcel;
